import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import apiWithAuth from '../../services/apiWithAuth';
import {
  fetchShopCoupons,
  createShopCoupon,
  updateShopCoupon,
  deleteShopCoupon,
  fetchShopProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  approveProduct,
  fetchCategoryTree
} from '../../services/api';
import CategoryTreeSelect from '../common/CategoryTreeSelect';
import ConfirmModal from '../common/ConfirmModal';
import GoogleMapsPicker from '../common/GoogleMapsPicker';
import '../common/CategoryTreeSelect.css';
import '../common/ConfirmModal.css';
import '../common/GoogleMapsPicker.css';
import './ShopManagement.css';

const SHOP_POLICY_OPTIONS = [
  {
    key: 'enableCODOption',
    label: 'COD',
    variant: 'cod',
    symbol: '💵',
  },
  {
    key: 'enableCancllationOption',
    label: 'Cancellation',
    variant: 'cancellation',
    symbol: '❌',
  },
  {
    key: 'enableReturnAndRefundOption',
    label: 'Return & Refund',
    variant: 'return',
    symbol: '↩',
  },
  {
    key: 'enableExchangeOption',
    label: 'Exchange',
    variant: 'exchange',
    symbol: '🔁',
  },
];

const ShopManagement = () => {
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Filter States
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    isVerified: '',
    shopActive: '',
    city: '',
    state: ''
  });

  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Sort State
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    order: 'desc'
  });

  // Products state
  const [products, setProducts] = useState([]);
  const [productPage, setProductPage] = useState(1);
  const [productLimit, setProductLimit] = useState(20);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productSearch, setProductSearch] = useState("");
  const [approved, setApproved] = useState(undefined);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [productSort, setProductSort] = useState("createdAt");
  const [productOrder, setProductOrder] = useState("desc");
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [productFormData, setProductFormData] = useState({
    name: "",
    description: "",
    price: "",
    mrp: "",
    category: "",
    tags: "",
    image: [],
    sponsored: false,
  });

  // Coupons state
  const [coupons, setCoupons] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterCustomerOnly, setFilterCustomerOnly] = useState(undefined);
  const [filterOnlineOnly, setFilterOnlineOnly] = useState(undefined);
  const [filterActive, setFilterActive] = useState(undefined);
  const [filterExpired, setFilterExpired] = useState(undefined);
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);
  const [editingCouponCode, setEditingCouponCode] = useState(null);
  const [couponFormData, setCouponFormData] = useState({
    couponCode: "",
    type: "percentage",
    discount: "",
    maxDiscount: "",
    minOrderValue: "",
    maxOrderValue: "",
    buyQuantity: "",
    getFreeQuantity: "",
    usesPerCustomer: 1,
    validFrom: "",
    validTo: "",
    showToCustomerOnly: false,
    validForOnlinePaymentsOnly: false,
  });

  // Confirm modal state for deletion
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shopToDelete, setShopToDelete] = useState(null);

  // Edit states
  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [isEditingShop, setIsEditingShop] = useState(false);
  const [editOwnerData, setEditOwnerData] = useState({});
  const [editShopData, setEditShopData] = useState({});

  // Toast state
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [togglingDelivery, setTogglingDelivery] = useState(false);
  const [togglingPickup, setTogglingPickup] = useState(false);
  const [togglingDeliveryFrom, setTogglingDeliveryFrom] = useState(false);
  const [togglingPolicyOption, setTogglingPolicyOption] = useState(null);

  const isDeliveryBlockedByPlan = selectedShop?.owner?.planType === 'F_MONTHLY';

  const getNowLocalDatetime = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

  // Toast functions
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 4000);
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: '' });
  };

  const canSubmitProduct = useMemo(() => {
    if (!productFormData.name || !productFormData.price || !productFormData.category) return false;
    return true;
  }, [productFormData]);

  // Load coupons for selected shop
  const loadCoupons = useCallback(async () => {
    if (!selectedShop) return;

    try {
      setLoading(true);
      setError(null);
      const res = await fetchShopCoupons(selectedShop._id, {
        type: filterType || undefined,
        showToCustomerOnly: filterCustomerOnly,
        validForOnlinePaymentsOnly: filterOnlineOnly,
        active: filterActive,
        expired: filterExpired,
      });
      setCoupons(res?.data?.data || []);
    } catch (err) {
      setError("Failed to load coupons");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedShop, filterType, filterCustomerOnly, filterOnlineOnly, filterActive, filterExpired]);

  // Load products for selected shop
  const loadProducts = useCallback(async () => {
    if (!selectedShop) return;

    try {
      setLoading(true);
      setError(null);
      const res = await fetchShopProducts(selectedShop._id, {
        page: productPage,
        limit: productLimit,
        search: productSearch || undefined,
        approved,
        includeInactive: includeInactive ? "true" : undefined,
        sort: productSort,
        order: productOrder,
      });
      const list = res?.data?.data || [];
      setProducts(list);
      const p = res?.data?.pagination;
      setProductTotalPages(p?.totalPages || 1);
    } catch (e) {
      setError("Failed to load products");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedShop, productPage, productLimit, productSearch, approved, includeInactive, productSort, productOrder]);

  // Fetch shop analytics
  const fetchAnalytics = useCallback(async () => {
    if (!selectedShop) return;

    setLoadingAnalytics(true);
    try {
      const response = await apiWithAuth.get(`/admin-panel/shops/${selectedShop._id}/analytics`);

      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [selectedShop]);

  // Fetch shops from API
  const fetchShops = useCallback(async (page = 1, newFilters = filters, newSort = sortConfig) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        sort: newSort.field,
        order: newSort.order,
        ...Object.fromEntries(
          Object.entries(newFilters).filter(([_, value]) => value !== '')
        )
      });

      const response = await apiWithAuth.get(`/admin-panel/shops?${queryParams}`);

      if (response.data.success) {
        setShops(response.data.data);
        setPagination(response.data.pagination);
        setSelectedShop(prev => {
          if (!prev) return prev;
          const updated = response.data.data.find(s => s._id === prev._id);
          return updated || prev;
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch shops');
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch shops');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, pagination.limit]);

  // Initial load
  useEffect(() => {
    fetchShops();
    loadCategories();
  }, [fetchShops]);

  useEffect(() => {
    if (selectedShop && activeTab === 'products') {
      loadProducts();
    }
  }, [selectedShop, activeTab, loadProducts]);

  useEffect(() => {
    if (selectedShop && activeTab === 'offers') {
      loadCoupons();
    }
  }, [selectedShop, activeTab, loadCoupons]);

  useEffect(() => {
    if (selectedShop && activeTab === 'analytics' && !analytics) {
      fetchAnalytics();
    }
  }, [selectedShop, activeTab, analytics, fetchAnalytics]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchShops(1, newFilters, sortConfig);
  };

  // Handle sorting
  const handleSort = (field) => {
    const newOrder = sortConfig.field === field && sortConfig.order === 'asc' ? 'desc' : 'asc';
    const newSort = { field, order: newOrder };
    setSortConfig(newSort);
    fetchShops(pagination.page, filters, newSort);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchShops(newPage, filters, sortConfig);
  };

  // Handle shop selection
  const handleShopSelect = (shop) => {
    setSelectedShop(shop);
    setActiveTab('overview');
    // Reset states when selecting a new shop
    setProducts([]);
    setCoupons([]);
    setAnalytics(null);
    setProductPage(1);
    setProductSearch("");
    setApproved(undefined);
    setIncludeInactive(false);
    setFilterType("");
    setFilterCustomerOnly(undefined);
    setFilterOnlineOnly(undefined);
    setFilterActive(undefined);
    setFilterExpired(undefined);
    setIsCreatingProduct(false);
    setIsCreatingCoupon(false);
    // Reset edit states
    setIsEditingOwner(false);
    setIsEditingShop(false);
    setEditOwnerData({});
    setEditShopData({});
    resetProductForm();
    resetCouponForm();
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const res = await fetchCategoryTree({ page: 1, limit: 1000 });
      const tree = res?.data?.data || [];
      setCategories(tree);
    } catch (e) {
      console.error('Failed to load categories', e);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Product form functions
  const resetProductForm = () => {
    setProductFormData({
      name: "",
      description: "",
      price: "",
      mrp: "",
      category: "",
      tags: "",
      image: [],
      sponsored: false,
    });
    setEditingProductId(null);
  };

  const onChangeProductField = (field, value) => {
    setProductFormData((prev) => ({ ...prev, [field]: value }));
  };

  const populateProductFormForEdit = (p) => {
    setIsCreatingProduct(true);
    setEditingProductId(p._id);
    setProductFormData({
      name: p.name || "",
      description: p.description || "",
      price: p.price ?? "",
      mrp: p.mrp != null && p.mrp !== "" ? String(p.mrp) : "",
      category: p.category?._id || p.category || "",
      tags: Array.isArray(p.tags) ? p.tags.join(", ") : "",
      image: Array.isArray(p.image) ? p.image : [],
      sponsored: !!p.sponsored,
    });
  };

  const buildProductBody = () => {
    const tags = (productFormData.tags || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const body = {
      shopId: selectedShop._id,
      name: productFormData.name.trim(),
      description: productFormData.description?.trim(),
      price: Number(productFormData.price),
      image: productFormData.image,
      category: productFormData.category,
      tags,
      sponsored: !!productFormData.sponsored,
      addBy: "admin",
    };
    const mrpStr = String(productFormData.mrp ?? "").trim();
    if (mrpStr !== "") {
      const mrpNum = Number(mrpStr);
      if (Number.isFinite(mrpNum)) body.mrp = mrpNum;
    }
    return body;
  };

  const handleCreateProduct = async () => {
    if (!canSubmitProduct) return;
    try {
      setLoading(true);
      setError(null);
      const body = buildProductBody();
      await createProduct(body);
      resetProductForm();
      setIsCreatingProduct(false);
      await loadProducts();
      showToast('Product created successfully!', 'success');
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create product";
      setError(msg);
      showToast(msg, 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProductId) return;
    try {
      setLoading(true);
      setError(null);
      const body = buildProductBody();
      await updateProduct(editingProductId, body);
      resetProductForm();
      setIsCreatingProduct(false);
      await loadProducts();
      showToast('Product updated successfully!', 'success');
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update product";
      setError(msg);
      showToast(msg, 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      setLoading(true);
      setError(null);
      await deleteProduct(id, { hard: false });
      await loadProducts();
      showToast('Product deleted successfully!', 'success');
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete product";
      setError(msg);
      showToast(msg, 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductApproval = async (p) => {
    try {
      setLoading(true);
      setError(null);
      await approveProduct(p._id, {
        isApproved: !p.isApproved,
        isRejected: false,
      });
      await loadProducts();
      showToast(`Product ${!p.isApproved ? 'approved' : 'unapproved'} successfully!`, 'success');
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update approval";
      setError(msg);
      showToast(msg, 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Coupon form functions
  const normalizeNumber = (val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    const n = Number(val);
    return Number.isNaN(n) ? undefined : n;
  };

  const resetCouponForm = () => {
    setCouponFormData({
      couponCode: "",
      type: "percentage",
      discount: "",
      maxDiscount: "",
      minOrderValue: "",
      maxOrderValue: "",
      buyQuantity: "",
      getFreeQuantity: "",
      usesPerCustomer: 1,
      validFrom: getNowLocalDatetime(),
      validTo: "",
      showToCustomerOnly: false,
      validForOnlinePaymentsOnly: false,
    });
    setEditingCouponCode(null);
  };

  const buildCreateCouponPayload = () => {
    const base = {
      shopId: selectedShop._id,
      userId: selectedShop.owner?._id || selectedShop.ownerId,
      couponCode: couponFormData.couponCode.trim(),
      type: couponFormData.type,
      validFrom: couponFormData.validFrom || getNowLocalDatetime(),
      validTo: couponFormData.validTo,
      showToCustomerOnly: !!couponFormData.showToCustomerOnly,
      validForOnlinePaymentsOnly: !!couponFormData.validForOnlinePaymentsOnly,
      usesPerCustomer: normalizeNumber(couponFormData.usesPerCustomer) ?? 1,
      minOrderValue: normalizeNumber(couponFormData.minOrderValue) ?? 0,
    };
    if (couponFormData.type === "percentage") {
      base.discount = normalizeNumber(couponFormData.discount);
      base.maxDiscount = normalizeNumber(couponFormData.maxDiscount);
    } else if (couponFormData.type === "flat") {
      base.discount = normalizeNumber(couponFormData.discount);
    } else if (couponFormData.type === "buyxgety") {
      base.buyQuantity = normalizeNumber(couponFormData.buyQuantity);
      base.getFreeQuantity = normalizeNumber(couponFormData.getFreeQuantity);
    }
    const maxOrder = normalizeNumber(couponFormData.maxOrderValue);
    if (maxOrder !== undefined) base.maxOrderValue = maxOrder;
    return base;
  };

  const buildUpdateCouponPayload = () => {
    const upd = {};
    upd.type = couponFormData.type;
    if (couponFormData.type === "percentage") {
      upd.discount = normalizeNumber(couponFormData.discount);
      upd.maxDiscount = normalizeNumber(couponFormData.maxDiscount);
    } else if (couponFormData.type === "flat") {
      upd.discount = normalizeNumber(couponFormData.discount);
    } else if (couponFormData.type === "buyxgety") {
      upd.buyQuantity = normalizeNumber(couponFormData.buyQuantity);
      upd.getFreeQuantity = normalizeNumber(couponFormData.getFreeQuantity);
    }
    const mov = normalizeNumber(couponFormData.minOrderValue);
    const xav = normalizeNumber(couponFormData.maxOrderValue);
    if (mov !== undefined) upd.minOrderValue = mov;
    if (xav !== undefined) upd.maxOrderValue = xav;
    const upc = normalizeNumber(couponFormData.usesPerCustomer);
    if (upc !== undefined) upd.usesPerCustomer = upc;
    if (couponFormData.validFrom) upd.validFrom = couponFormData.validFrom;
    if (couponFormData.validTo) upd.validTo = couponFormData.validTo;
    upd.showToCustomerOnly = !!couponFormData.showToCustomerOnly;
    upd.validForOnlinePaymentsOnly = !!couponFormData.validForOnlinePaymentsOnly;
    return upd;
  };

  const handleCreateCoupon = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!couponFormData.couponCode || !couponFormData.type || !couponFormData.validTo) {
        const errorMsg = "Please fill all required fields.";
        setError(errorMsg);
        showToast(errorMsg, 'error');
        setLoading(false);
        return;
      }
      const fromISO = (couponFormData.validFrom || getNowLocalDatetime()).replace(" ", "T");
      const toISO = couponFormData.validTo.replace(" ", "T");
      const fromDate = new Date(fromISO);
      const toDate = new Date(toISO);
      if (!(fromDate < toDate)) {
        const errorMsg = "'Valid From' must be before 'Valid To'.";
        setError(errorMsg);
        showToast(errorMsg, 'error');
        setLoading(false);
        return;
      }
      const payload = buildCreateCouponPayload();
      await createShopCoupon(payload);
      resetCouponForm();
      setIsCreatingCoupon(false);
      await loadCoupons();
      showToast('Coupon created successfully!', 'success');
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to create coupon";
      setError(message);
      showToast(message, 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCoupon = async () => {
    if (!editingCouponCode) return;
    try {
      setLoading(true);
      setError(null);
      const updateBody = buildUpdateCouponPayload();
      await updateShopCoupon(selectedShop._id, editingCouponCode, updateBody);
      resetCouponForm();
      setIsCreatingCoupon(false);
      await loadCoupons();
      showToast('Coupon updated successfully!', 'success');
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to update coupon";
      setError(message);
      showToast(message, 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      setLoading(true);
      setError(null);
      await deleteShopCoupon(selectedShop._id, code);
      await loadCoupons();
      showToast('Coupon deleted successfully!', 'success');
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to delete coupon";
      setError(message);
      showToast(message, 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const populateCouponFormForEdit = (c) => {
    setIsCreatingCoupon(true);
    setEditingCouponCode(c.couponCode);
    setCouponFormData({
      couponCode: c.couponCode,
      type: c.type,
      discount: c.discount ?? "",
      maxDiscount: c.maxDiscount ?? "",
      minOrderValue: c.minOrderValue ?? "",
      maxOrderValue: c.maxOrderValue ?? "",
      buyQuantity: c.buyQuantity ?? "",
      getFreeQuantity: c.getFreeQuantity ?? "",
      usesPerCustomer: c.usesPerCustomer ?? 1,
      validFrom: c.validFrom
        ? new Date(c.validFrom).toISOString().slice(0, 16)
        : "",
      validTo: c.validTo ? new Date(c.validTo).toISOString().slice(0, 16) : "",
      showToCustomerOnly: !!c.showToCustomerOnly,
      validForOnlinePaymentsOnly: !!c.validForOnlinePaymentsOnly,
    });
  };

  const onChangeCouponField = (field, value) => {
    setCouponFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmitCoupon = () => {
    if (!couponFormData.couponCode && !editingCouponCode) return false;
    if (!couponFormData.type) return false;
    if (!couponFormData.validFrom || !couponFormData.validTo) return false;
    if (couponFormData.type === "percentage") {
      return couponFormData.discount !== "" && couponFormData.maxDiscount !== "";
    }
    if (couponFormData.type === "flat") {
      return couponFormData.discount !== "";
    }
    if (couponFormData.type === "buyxgety") {
      return couponFormData.buyQuantity !== "" && couponFormData.getFreeQuantity !== "";
    }
    return true;
  };

  // Shop deletion functions
  const handleShopDelete = (shop) => {
    console.log('Delete button clicked for shop:', shop);
    if (!shop) {
      console.log('No shop provided');
      return;
    }
    setShopToDelete(shop);
    setConfirmOpen(true);
    console.log('Modal should open now');
  };

  const confirmDelete = async () => {
    if (!shopToDelete) return;

    console.log('User confirmed deletion, making API call...');
    setLoading(true);
    setError(null);

    try {
      const response = await apiWithAuth.delete(`/shops/${shopToDelete._id}`);
      console.log('Delete API response:', response);

      if (response.data && response.data.success) {
        console.log('Shop deleted successfully');
        setShops(prev => prev.filter(s => s._id !== shopToDelete._id));
        setConfirmOpen(false);
        setShopToDelete(null);
        // Update pagination total
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
        showToast('Shop deleted successfully!', 'success');
      } else {
        throw new Error(response.data?.message || 'Failed to delete shop');
      }
    } catch (err) {
      console.error('Error deleting shop:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete shop';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setShopToDelete(null);
  };

  // Edit functions
  const handleEditOwner = () => {
    setEditOwnerData({
      fullName: selectedShop.owner?.fullName || '',
      email: selectedShop.owner?.email || '',
      phone: selectedShop.owner?.phone || ''
    });
    setIsEditingOwner(true);
  };

  const handleEditShop = () => {
    setEditShopData({
      name: selectedShop.name || '',
      description: selectedShop.description || '',
      contactPhone: selectedShop.contactPhone || '',
      shopPhone: selectedShop.shopPhone || '',
      contactEmail: selectedShop.contactEmail || '',
      sellerType: selectedShop.sellerType || '',
      gst: selectedShop.gst || '',
      openingTime: selectedShop.openingTime || '',
      closingTime: selectedShop.closingTime || '',
      businessDays: selectedShop.businessDays || [],
      enableDeliveryOption: isDeliveryBlockedByPlan ? false : selectedShop.enableDeliveryOption !== false,
      enablePickupOption: selectedShop.enablePickupOption !== false,
      deliveryFrom: selectedShop.deliveryFrom || 'SELF',
      baseDeliveryFee: selectedShop.baseDeliveryFee || 0,
      baseDeliveryDistanceKm: selectedShop.baseDeliveryDistanceKm || 0,
      perKmDeliveryFee: selectedShop.perKmDeliveryFee || 0,
      maxDeliveryDistanceKm: selectedShop.maxDeliveryDistanceKm || 0,
      freeDeliveryMinOrderValue: selectedShop.freeDeliveryMinOrderValue || 0,
      freeDeliveryMinOrderValue2: selectedShop.freeDeliveryMinOrderValue2 || 0,
      shippingCharges: selectedShop.shippingCharges || 0,
      enableCODOption:
        selectedShop.enableCODOption === true || selectedShop.enableCOD === true,
      enableCancllationOption:
        selectedShop.enableCancllationOption === true || selectedShop.enableCancellation === true,
      enableReturnAndRefundOption:
        selectedShop.enableReturnAndRefundOption === true ||
        selectedShop.enableReturn === true ||
        selectedShop.enableRefund === true,
      enableExchangeOption:
        selectedShop.enableExchangeOption === true ||
        selectedShop.enableExchange === true ||
        selectedShop.enableExchangeRefund === true,
      // Location data
      locationData: {
        address: selectedShop.address?.address || '',
        buildingNo: selectedShop.address?.buildingNo || '',
        street: selectedShop.address?.street || '',
        city: selectedShop.address?.city || '',
        state: selectedShop.address?.state || '',
        pincode: selectedShop.address?.pincode || '',
        latitude: selectedShop.address?.latitude,
        longitude: selectedShop.address?.longitude,
      }
    });
    setIsEditingShop(true);
  };

  const handleSaveOwner = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiWithAuth.put(`/admin-panel/onboarding/profile?userId=${selectedShop.owner._id}`, editOwnerData);

      if (response.data.success) {
        // Update the selected shop with new owner data
        setSelectedShop(prev => ({
          ...prev,
          owner: {
            ...prev.owner,
            ...editOwnerData
          }
        }));
        setIsEditingOwner(false);
        showToast('Owner details updated successfully!', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to update owner details');
      }
    } catch (err) {
      console.error('Error updating owner:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update owner details';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDelivery = async () => {
    if (!selectedShop || togglingDelivery) return;
    if (selectedShop.owner?.planType === 'F_MONTHLY') {
      showToast('Delivery option is disabled for F_MONTHLY plan', 'error');
      return;
    }
    const newValue = !(selectedShop.enableDeliveryOption !== false);
    setTogglingDelivery(true);
    try {
      setError(null);
      const shopUpdateData = {
        name: selectedShop.name,
        description: selectedShop.description,
        contactPhone: selectedShop.contactPhone,
        shopPhone: selectedShop.shopPhone,
        contactEmail: selectedShop.contactEmail,
        sellerType: selectedShop.sellerType,
        gst: selectedShop.gst,
        openingTime: selectedShop.openingTime,
        closingTime: selectedShop.closingTime,
        businessDays: selectedShop.businessDays || [],
        enableDeliveryOption: newValue,
        enablePickupOption: selectedShop.enablePickupOption !== false,
        deliveryFrom: selectedShop.deliveryFrom || 'SELF',
        baseDeliveryFee: Number(selectedShop.baseDeliveryFee) || 0,
        baseDeliveryDistanceKm: Number(selectedShop.baseDeliveryDistanceKm) || 0,
        perKmDeliveryFee: Number(selectedShop.perKmDeliveryFee) || 0,
        maxDeliveryDistanceKm: Number(selectedShop.maxDeliveryDistanceKm) || 0,
        freeDeliveryMinOrderValue: Number(selectedShop.freeDeliveryMinOrderValue) || 0,
        freeDeliveryMinOrderValue2: Number(selectedShop.freeDeliveryMinOrderValue2) || 0,
        shippingCharges: Number(selectedShop.shippingCharges) || 0,
        // Address fields sent directly
        buildingNo: selectedShop.buildingNo || '',
        street: selectedShop.street || '',
        floor: selectedShop.floor || '',
        landmark: selectedShop.landmark || '',
        city: selectedShop.city || '',
        state: selectedShop.state || '',
        pincode: selectedShop.pincode || '',
        latitude: selectedShop.latitude,
        longitude: selectedShop.longitude,
      };
      const response = await apiWithAuth.put(`/shops/${selectedShop._id}`, shopUpdateData);
      if (response.data.success) {
        setSelectedShop(prev => ({ ...prev, enableDeliveryOption: newValue }));
        setShops(prev => prev.map(s => s._id === selectedShop._id ? { ...s, enableDeliveryOption: newValue } : s));
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (err) {
      console.error('Error toggling delivery:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update delivery';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setTogglingDelivery(false);
    }
  };

  const handleToggleDeliveryFrom = async () => {
    if (!selectedShop || togglingDeliveryFrom) return;

    if (selectedShop.owner?.planType === 'F_MONTHLY') {
      showToast('Delivery is disabled for F_MONTHLY plan', 'error');
      return;
    }

    if (selectedShop.enableDeliveryOption === false) {
      showToast('Enable delivery to change delivery source', 'error');
      return;
    }

    const newDeliveryFrom = (selectedShop.deliveryFrom || 'SELF') === 'KD' ? 'SELF' : 'KD';
    setTogglingDeliveryFrom(true);

    try {
      setError(null);
      const shopUpdateData = {
        name: selectedShop.name,
        description: selectedShop.description,
        contactPhone: selectedShop.contactPhone,
        shopPhone: selectedShop.shopPhone,
        contactEmail: selectedShop.contactEmail,
        sellerType: selectedShop.sellerType,
        gst: selectedShop.gst,
        openingTime: selectedShop.openingTime,
        closingTime: selectedShop.closingTime,
        businessDays: selectedShop.businessDays || [],
        enableDeliveryOption: selectedShop.enableDeliveryOption !== false,
        enablePickupOption: selectedShop.enablePickupOption !== false,
        deliveryFrom: newDeliveryFrom,
        baseDeliveryFee: Number(selectedShop.baseDeliveryFee) || 0,
        baseDeliveryDistanceKm: Number(selectedShop.baseDeliveryDistanceKm) || 0,
        perKmDeliveryFee: Number(selectedShop.perKmDeliveryFee) || 0,
        maxDeliveryDistanceKm: Number(selectedShop.maxDeliveryDistanceKm) || 0,
        freeDeliveryMinOrderValue: Number(selectedShop.freeDeliveryMinOrderValue) || 0,
        freeDeliveryMinOrderValue2: Number(selectedShop.freeDeliveryMinOrderValue2) || 0,
        shippingCharges: Number(selectedShop.shippingCharges) || 0,
        // Address fields sent directly
        buildingNo: selectedShop.buildingNo || '',
        street: selectedShop.street || '',
        floor: selectedShop.floor || '',
        landmark: selectedShop.landmark || '',
        city: selectedShop.city || '',
        state: selectedShop.state || '',
        pincode: selectedShop.pincode || '',
        latitude: selectedShop.latitude,
        longitude: selectedShop.longitude,
      };

      const response = await apiWithAuth.put(`/shops/${selectedShop._id}`, shopUpdateData);
      if (response.data.success) {
        setSelectedShop(prev => ({ ...prev, deliveryFrom: newDeliveryFrom }));
        setShops(prev => prev.map(s => s._id === selectedShop._id ? { ...s, deliveryFrom: newDeliveryFrom } : s));
      } else {
        throw new Error(response.data.message || 'Failed to update delivery source');
      }
    } catch (err) {
      console.error('Error toggling deliveryFrom:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update delivery source';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setTogglingDeliveryFrom(false);
    }
  };

  const handleTogglePickup = async () => {
    if (!selectedShop || togglingPickup) return;
    const newValue = !(selectedShop.enablePickupOption !== false);
    setTogglingPickup(true);
    try {
      setError(null);
      const shopUpdateData = {
        name: selectedShop.name,
        description: selectedShop.description,
        contactPhone: selectedShop.contactPhone,
        shopPhone: selectedShop.shopPhone,
        contactEmail: selectedShop.contactEmail,
        sellerType: selectedShop.sellerType,
        gst: selectedShop.gst,
        openingTime: selectedShop.openingTime,
        closingTime: selectedShop.closingTime,
        businessDays: selectedShop.businessDays || [],
        enableDeliveryOption: selectedShop.enableDeliveryOption !== false,
        enablePickupOption: newValue,
        deliveryFrom: selectedShop.deliveryFrom || 'SELF',
        baseDeliveryFee: Number(selectedShop.baseDeliveryFee) || 0,
        baseDeliveryDistanceKm: Number(selectedShop.baseDeliveryDistanceKm) || 0,
        perKmDeliveryFee: Number(selectedShop.perKmDeliveryFee) || 0,
        maxDeliveryDistanceKm: Number(selectedShop.maxDeliveryDistanceKm) || 0,
        freeDeliveryMinOrderValue: Number(selectedShop.freeDeliveryMinOrderValue) || 0,
        freeDeliveryMinOrderValue2: Number(selectedShop.freeDeliveryMinOrderValue2) || 0,
        shippingCharges: Number(selectedShop.shippingCharges) || 0,
        // Address fields sent directly
        buildingNo: selectedShop.buildingNo || '',
        street: selectedShop.street || '',
        floor: selectedShop.floor || '',
        landmark: selectedShop.landmark || '',
        city: selectedShop.city || '',
        state: selectedShop.state || '',
        pincode: selectedShop.pincode || '',
        latitude: selectedShop.latitude,
        longitude: selectedShop.longitude,
      };
      const response = await apiWithAuth.put(`/shops/${selectedShop._id}`, shopUpdateData);
      if (response.data.success) {
        setSelectedShop(prev => ({ ...prev, enablePickupOption: newValue }));
        setShops(prev => prev.map(s => s._id === selectedShop._id ? { ...s, enablePickupOption: newValue } : s));
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (err) {
      console.error('Error toggling pickup:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update pickup';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setTogglingPickup(false);
    }
  };

  const handleTogglePolicyOption = async (fieldKey) => {
    if (!selectedShop || togglingPolicyOption === fieldKey) return;
    const newValue = !(selectedShop[fieldKey] === true);
    setTogglingPolicyOption(fieldKey);
    try {
      setError(null);
      const shopUpdateData = {
        name: selectedShop.name,
        description: selectedShop.description,
        contactPhone: selectedShop.contactPhone,
        shopPhone: selectedShop.shopPhone,
        contactEmail: selectedShop.contactEmail,
        sellerType: selectedShop.sellerType,
        gst: selectedShop.gst,
        openingTime: selectedShop.openingTime,
        closingTime: selectedShop.closingTime,
        businessDays: selectedShop.businessDays || [],
        enableDeliveryOption: selectedShop.enableDeliveryOption !== false,
        enablePickupOption: selectedShop.enablePickupOption !== false,
        deliveryFrom: selectedShop.deliveryFrom || 'SELF',
        baseDeliveryFee: Number(selectedShop.baseDeliveryFee) || 0,
        baseDeliveryDistanceKm: Number(selectedShop.baseDeliveryDistanceKm) || 0,
        perKmDeliveryFee: Number(selectedShop.perKmDeliveryFee) || 0,
        maxDeliveryDistanceKm: Number(selectedShop.maxDeliveryDistanceKm) || 0,
        freeDeliveryMinOrderValue: Number(selectedShop.freeDeliveryMinOrderValue) || 0,
        freeDeliveryMinOrderValue2: Number(selectedShop.freeDeliveryMinOrderValue2) || 0,
        shippingCharges: Number(selectedShop.shippingCharges) || 0,
        enableCODOption: fieldKey === 'enableCODOption' ? newValue : selectedShop.enableCODOption === true,
        enableCancllationOption:
          fieldKey === 'enableCancllationOption' ? newValue : selectedShop.enableCancllationOption === true,
        enableReturnAndRefundOption:
          fieldKey === 'enableReturnAndRefundOption'
            ? newValue
            : selectedShop.enableReturnAndRefundOption === true,
        enableExchangeOption:
          fieldKey === 'enableExchangeOption' ? newValue : selectedShop.enableExchangeOption === true,
        buildingNo: selectedShop.buildingNo || '',
        street: selectedShop.street || '',
        floor: selectedShop.floor || '',
        landmark: selectedShop.landmark || '',
        city: selectedShop.city || '',
        state: selectedShop.state || '',
        pincode: selectedShop.pincode || '',
        latitude: selectedShop.latitude,
        longitude: selectedShop.longitude,
      };
      const response = await apiWithAuth.put(`/shops/${selectedShop._id}`, shopUpdateData);
      if (response.data.success) {
        setSelectedShop(prev => ({ ...prev, [fieldKey]: newValue }));
        setShops(prev =>
          prev.map(s => (s._id === selectedShop._id ? { ...s, [fieldKey]: newValue } : s))
        );
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (err) {
      console.error(`Error toggling ${fieldKey}:`, err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update option';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setTogglingPolicyOption(null);
    }
  };

  const handleSaveShop = async () => {
    try {
      setLoading(true);
      setError(null);

      // On the shop fees & settings tab we suppress the generic "Shop details updated successfully"
      // toast to avoid showing the wrong success message for fee/settings updates.
      if (activeTab === 'fees-settings') {
        hideToast();
      }

      // Prepare shop data - send address fields directly, not in address object
      const shopUpdateData = {
        name: editShopData.name,
        description: editShopData.description,
        contactPhone: editShopData.contactPhone,
        shopPhone: editShopData.shopPhone,
        contactEmail: editShopData.contactEmail,
        sellerType: editShopData.sellerType,
        gst: editShopData.gst,
        openingTime: editShopData.openingTime,
        closingTime: editShopData.closingTime,
        businessDays: editShopData.businessDays,
        enableDeliveryOption: selectedShop.owner?.planType === 'F_MONTHLY'
          ? false
          : Boolean(editShopData.enableDeliveryOption),
        enablePickupOption: Boolean(editShopData.enablePickupOption),
        deliveryFrom: editShopData.deliveryFrom || 'SELF',
        baseDeliveryFee: Number(editShopData.baseDeliveryFee),
        baseDeliveryDistanceKm: Number(editShopData.baseDeliveryDistanceKm),
        perKmDeliveryFee: Number(editShopData.perKmDeliveryFee),
        maxDeliveryDistanceKm: Number(editShopData.maxDeliveryDistanceKm),
        freeDeliveryMinOrderValue: Number(editShopData.freeDeliveryMinOrderValue),
        freeDeliveryMinOrderValue2: Number(editShopData.freeDeliveryMinOrderValue2),
        shippingCharges: Number(editShopData.shippingCharges),
        enableCODOption: Boolean(editShopData.enableCODOption),
        enableCancllationOption: Boolean(editShopData.enableCancllationOption),
        enableReturnAndRefundOption: Boolean(editShopData.enableReturnAndRefundOption),
        enableExchangeOption: Boolean(editShopData.enableExchangeOption),
        // Address fields sent directly, not in address object
        buildingNo: editShopData.locationData?.buildingNo || '',
        street: editShopData.locationData?.street || '',
        floor: editShopData.locationData?.floor || '',
        landmark: editShopData.locationData?.landmark || '',
        city: editShopData.locationData?.city || '',
        state: editShopData.locationData?.state || '',
        pincode: editShopData.locationData?.pincode || '',
        latitude: editShopData.locationData?.latitude,
        longitude: editShopData.locationData?.longitude,
      };

      const response = await apiWithAuth.put(`/shops/${selectedShop._id}`, shopUpdateData);

      if (response.data.success) {
        // Update the selected shop with new data
        const policyOptionUpdate = {
          enableCODOption: Boolean(editShopData.enableCODOption),
          enableCancllationOption: Boolean(editShopData.enableCancllationOption),
          enableReturnAndRefundOption: Boolean(editShopData.enableReturnAndRefundOption),
          enableExchangeOption: Boolean(editShopData.enableExchangeOption),
        };
        setSelectedShop(prev => ({
          ...prev,
          ...shopUpdateData,
          ...policyOptionUpdate,
        }));
        setShops(prev => prev.map(s => (s._id === selectedShop._id ? { ...s, ...policyOptionUpdate } : s)));
        setIsEditingShop(false);
        if (activeTab !== 'fees-settings') {
          showToast('Shop details updated successfully!', 'success');
        } else {
          hideToast();
        }
      } else {
        throw new Error(response.data.message || 'Failed to update shop details');
      }
    } catch (err) {
      console.error('Error updating shop:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update shop details';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingOwner(false);
    setIsEditingShop(false);
    setEditOwnerData({});
    setEditShopData({});
  };

  // Helper function for sort icons
  const getSortIcon = (field) => {
    if (sortConfig.field !== field) return '↕';
    return sortConfig.order === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="shop-management-wrapper">
      <div className="shop-management">

        {/* Header Section */}
        <div className="shop-management-header">
          <div>
            <h1>Shop Management</h1>
            <p>Manage and monitor registered shops</p>
          </div>
          <div className="header-actions">
            <span className="text-sm text-gray-600">
              {selectedShop ? (
                activeTab === 'products' ? `${products.length} products` :
                  activeTab === 'offers' ? `${coupons.length} coupons` :
                    activeTab === 'analytics' ? 'Analytics Dashboard' :
                      activeTab === 'fees-settings' ? 'Shop fees & delivery settings' :
                      activeTab === 'shop-members' ? `${selectedShop.shopMembersCount ?? selectedShop.shopMembers?.length ?? 0} shop members` :
                      activeTab === 'delivery-boys' ? `${selectedShop.deliveryBoysCount ?? selectedShop.deliveryBoys?.length ?? 0} delivery boys` :
                      activeTab === 'field-boys' ? `${selectedShop.fieldBoysCount ?? selectedShop.fieldBoys?.length ?? 0} field boys` :
                      activeTab === 'bloggers' ? `${selectedShop.bloggersCount ?? selectedShop.bloggers?.length ?? 0} bloggers` :
                      'Shop Details'
              ) : `${pagination.total} total shops`}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {error}
          </div>
        )}

        {!selectedShop ? (
          <>
            {/* Filters Toolbar */}
            <div className="filters-toolbar">
              <div className="filter-group">
                <label className="filter-label">Search</label>
                <input
                  type="text"
                  placeholder="Search shops..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })}
                  className="filter-input search-input"
                />
              </div>

              <div className="toolbar-divider"></div>

              <div className="filter-group">
                <label className="filter-label">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
                  className="filter-select"
                >
                  <option value="">All Status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="HOLD">Hold</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">City</label>
                <input
                  type="text"
                  placeholder="City"
                  value={filters.city}
                  onChange={(e) => handleFilterChange({ ...filters, city: e.target.value })}
                  className="filter-input"
                />
              </div>

              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.isVerified === 'true'}
                  onChange={(e) => handleFilterChange({
                    ...filters,
                    isVerified: e.target.checked ? 'true' : ''
                  })}
                />
                Verified Only
              </label>

              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.shopActive === 'true'}
                  onChange={(e) => handleFilterChange({
                    ...filters,
                    shopActive: e.target.checked ? 'true' : ''
                  })}
                />
                Active Only
              </label>
            </div>

            {/* Main Data Area */}
            <div className="data-area">
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('name')}>
                        Shop Name {getSortIcon('name')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('owner')}>
                        Owner {getSortIcon('owner')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('city')}>
                        Location {getSortIcon('city')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('status')}>
                        Status {getSortIcon('status')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('isVerified')}>
                        Verified {getSortIcon('isVerified')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('shopActive')}>
                        Active {getSortIcon('shopActive')}
                      </th>
                      <th>Delivery</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8">
                          <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading shops...</p>
                          </div>
                        </td>
                      </tr>
                    ) : shops.length === 0 ? (
                      <tr>
                        <td colSpan="8">
                          <div className="empty-state">
                            <div className="empty-icon">🏪</div>
                            <div className="empty-title">No shops found</div>
                            <div className="empty-description">
                              Try adjusting your filters or search criteria
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      shops.map((shop) => (
                        <tr key={shop._id} onClick={() => handleShopSelect(shop)} style={{ cursor: 'pointer' }}>
                          <td>
                            <div className="flex items-center gap-3">
                              <img
                                src={shop.logoUrl || '/KD-icon.png'}
                                alt={shop.name}
                                className="sidebar-item-logo"
                                onError={(e) => { e.target.src = '/KD-icon.png'; }}
                              />
                              <div>
                                <div className="font-medium text-gray-900">{shop.name}</div>
                                <div className="text-xs text-gray-500">{shop.description}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="font-medium">{shop.owner?.fullName || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{shop.owner?.email || 'N/A'}</div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div>{shop.address?.city}, {shop.address?.state}</div>
                              <div className="text-xs text-gray-500">{shop.address?.pincode}</div>
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge status-${shop.status?.toLowerCase()}`}>
                              {shop.status}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${shop.isVerified ? 'status-approved' : 'status-pending'}`}>
                              {shop.isVerified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${shop.shopActive ? 'status-approved' : 'status-rejected'}`}>
                              {shop.shopActive ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${shop.enableDeliveryOption === true ? 'status-approved' : 'status-rejected'}`}>
                              {shop.enableDeliveryOption === true ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShopSelect(shop);
                                }}
                              >
                                View
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleShopDelete(shop);
                                }}
                                type="button"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <div className="pagination-info">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} shops
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="pagination-btn"
                  >
                    Previous
                  </button>

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Shop Details Header */}
            <div className="shop-details-header">
              <button onClick={() => setSelectedShop(null)} className="btn-back">
                ← Back to Shops
              </button>
              <div className="shop-info">
                <img
                  src={selectedShop.logoUrl || '/KD-icon.png'}
                  alt={selectedShop.name}
                  className="shop-logo-small"
                  onError={(e) => { e.target.src = '/KD-icon.png'; }}
                />
                <div>
                  <h2>{selectedShop.name}</h2>
                  <p>{selectedShop.address?.city}, {selectedShop.address?.state}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="shop-details-tabs">
              <button
                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`tab ${activeTab === 'fees-settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('fees-settings')}
              >
                Shop fees & settings
              </button>
              <button
                className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setActiveTab('analytics')}
              >
                Analytics
              </button>
              {/* <button
                className={`tab ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                Products
              </button> */}
              <button
                className={`tab ${activeTab === 'shop-members' ? 'active' : ''}`}
                onClick={() => setActiveTab('shop-members')}
              >
                Shop Members
                {(selectedShop.shopMembersCount ?? selectedShop.shopMembers?.length ?? 0) > 0 && (
                  <span className="tab-count">{selectedShop.shopMembersCount ?? selectedShop.shopMembers?.length ?? 0}</span>
                )}
              </button>
              <button
                className={`tab ${activeTab === 'delivery-boys' ? 'active' : ''}`}
                onClick={() => setActiveTab('delivery-boys')}
              >
                Delivery Boys
                {(selectedShop.deliveryBoysCount ?? selectedShop.deliveryBoys?.length ?? 0) > 0 && (
                  <span className="tab-count">{selectedShop.deliveryBoysCount ?? selectedShop.deliveryBoys?.length ?? 0}</span>
                )}
              </button>
              <button
                className={`tab ${activeTab === 'field-boys' ? 'active' : ''}`}
                onClick={() => setActiveTab('field-boys')}
              >
                Field Boy
                {(selectedShop.fieldBoysCount ?? selectedShop.fieldBoys?.length ?? 0) > 0 && (
                  <span className="tab-count">{selectedShop.fieldBoysCount ?? selectedShop.fieldBoys?.length ?? 0}</span>
                )}
              </button>
              <button
                className={`tab ${activeTab === 'bloggers' ? 'active' : ''}`}
                onClick={() => setActiveTab('bloggers')}
              >
                Blogger
                {(selectedShop.bloggersCount ?? selectedShop.bloggers?.length ?? 0) > 0 && (
                  <span className="tab-count">{selectedShop.bloggersCount ?? selectedShop.bloggers?.length ?? 0}</span>
                )}
              </button>
              <button
                className={`tab ${activeTab === 'offers' ? 'active' : ''}`}
                onClick={() => setActiveTab('offers')}
              >
                Offers
              </button>
            </div>

            {/* Tab Content */}
            <div className="shop-details-content">
              {activeTab === 'overview' && (
                <div className="shop-overview">
                  <div className="shop-details-grid">
                    {/* Owner Information */}
                    <div className="detail-section">
                      <div className="section-header">
                        <h3>Owner Information</h3>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleEditOwner}
                          disabled={isEditingOwner}
                        >
                          {isEditingOwner ? 'Editing...' : 'Edit'}
                        </button>
                      </div>

                      {isEditingOwner ? (
                        <div className="edit-form">
                          <div className="form-group">
                            <label className="form-label">Full Name</label>
                            <input
                              type="text"
                              value={editOwnerData.fullName}
                              onChange={(e) => setEditOwnerData(prev => ({ ...prev, fullName: e.target.value }))}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Email</label>
                            <input
                              type="email"
                              value={editOwnerData.email}
                              onChange={(e) => setEditOwnerData(prev => ({ ...prev, email: e.target.value }))}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Phone</label>
                            <input
                              type="text"
                              value={editOwnerData.phone}
                              onChange={(e) => setEditOwnerData(prev => ({ ...prev, phone: e.target.value }))}
                              className="form-input"
                            />
                          </div>
                          <div className="form-actions">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={handleSaveOwner}
                              disabled={loading}
                            >
                              {loading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-items">
                          <div className="detail-item">
                            <span className="label">Name:</span>
                            <span className="value">{selectedShop.owner?.fullName || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Email:</span>
                            <span className="value">{selectedShop.owner?.email || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Phone:</span>
                            <span className="value">{selectedShop.owner?.phone || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Shop Information */}
                    <div className="detail-section">
                      <div className="section-header">
                        <h3>Shop Information</h3>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleEditShop}
                          disabled={isEditingShop}
                        >
                          {isEditingShop ? 'Editing...' : 'Edit'}
                        </button>
                      </div>

                      {isEditingShop ? (
                        <div className="edit-form">
                          <div className="form-grid">
                            <div className="form-group">
                              <label className="form-label">Shop Name</label>
                              <input
                                type="text"
                                value={editShopData.name}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, name: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Description</label>
                              <textarea
                                value={editShopData.description}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, description: e.target.value }))}
                                className="form-textarea"
                                rows={2}
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Contact Phone</label>
                              <input
                                type="text"
                                value={editShopData.contactPhone}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, contactPhone: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Shop Phone</label>
                              <input
                                type="text"
                                value={editShopData.shopPhone}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, shopPhone: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Contact Email</label>
                              <input
                                type="email"
                                value={editShopData.contactEmail}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, contactEmail: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Seller Type</label>
                              <select
                                value={editShopData.sellerType}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, sellerType: e.target.value }))}
                                className="form-select"
                              >
                                <option value="">Select Type</option>
                                <option value="individual">Individual</option>
                                <option value="business">Business</option>
                                <option value="company">Company</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">GST Number</label>
                              <input
                                type="text"
                                value={editShopData.gst}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, gst: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Opening Time</label>
                              <input
                                type="time"
                                value={editShopData.openingTime}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, openingTime: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Closing Time</label>
                              <input
                                type="time"
                                value={editShopData.closingTime}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, closingTime: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                          </div>
                          <div className="form-actions">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={handleSaveShop}
                              disabled={loading}
                            >
                              {loading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-items">
                          <div className="detail-item">
                            <span className="label">Name:</span>
                            <span className="value">{selectedShop.name || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Description:</span>
                            <span className="value">{selectedShop.description || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Contact Phone:</span>
                            <span className="value">{selectedShop.contactPhone || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Shop Phone:</span>
                            <span className="value">{selectedShop.shopPhone || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Contact Email:</span>
                            <span className="value">{selectedShop.contactEmail || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Seller Type:</span>
                            <span className="value">{selectedShop.sellerType || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">GST:</span>
                            <span className="value">{selectedShop.gst || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Opening Time:</span>
                            <span className="value">{selectedShop.openingTime || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Closing Time:</span>
                            <span className="value">{selectedShop.closingTime || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Address Information */}
                    <div className="detail-section">
                      <h3>Address</h3>
                      {isEditingShop ? (
                        <div className="edit-form">
                          <div className="form-group full-width">
                            <label className="form-label">Location *</label>
                            <GoogleMapsPicker
                              value={editShopData.locationData || {}}
                              onChange={(locationData) => {
                                setEditShopData(prev => ({
                                  ...prev,
                                  locationData
                                }));
                              }}
                              placeholder="Search for shop location..."
                              disabled={false}
                            />
                          </div>
                          <div className="form-grid">
                            <div className="form-group">
                              <label className="form-label">Building No</label>
                              <input
                                type="text"
                                value={editShopData.locationData?.buildingNo || ''}
                                onChange={(e) => setEditShopData(prev => ({
                                  ...prev,
                                  locationData: { ...prev.locationData, buildingNo: e.target.value }
                                }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Street</label>
                              <input
                                type="text"
                                value={editShopData.locationData?.street || ''}
                                onChange={(e) => setEditShopData(prev => ({
                                  ...prev,
                                  locationData: { ...prev.locationData, street: e.target.value }
                                }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">City</label>
                              <input
                                type="text"
                                value={editShopData.locationData?.city || ''}
                                onChange={(e) => setEditShopData(prev => ({
                                  ...prev,
                                  locationData: { ...prev.locationData, city: e.target.value }
                                }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">State</label>
                              <input
                                type="text"
                                value={editShopData.locationData?.state || ''}
                                onChange={(e) => setEditShopData(prev => ({
                                  ...prev,
                                  locationData: { ...prev.locationData, state: e.target.value }
                                }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Pincode</label>
                              <input
                                type="text"
                                value={editShopData.locationData?.pincode || ''}
                                onChange={(e) => setEditShopData(prev => ({
                                  ...prev,
                                  locationData: { ...prev.locationData, pincode: e.target.value }
                                }))}
                                className="form-input"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-items">
                          <div className="detail-item">
                            <span className="label">Address:</span>
                            <span className="value">{selectedShop.address?.address || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Building:</span>
                            <span className="value">{selectedShop.address?.buildingNo || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Street:</span>
                            <span className="value">{selectedShop.address?.street || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">City:</span>
                            <span className="value">{selectedShop.address?.city || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">State:</span>
                            <span className="value">{selectedShop.address?.state || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Pincode:</span>
                            <span className="value">{selectedShop.address?.pincode || 'N/A'}</span>
                          </div>
                          {selectedShop.address?.latitude && selectedShop.address?.longitude && (
                            <div className="detail-item">
                              <span className="label">Coordinates:</span>
                              <span className="value">
                                📍 {selectedShop.address.latitude.toFixed(6)}, {selectedShop.address.longitude.toFixed(6)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Business Information */}
                    <div className="detail-section">
                      <h3>Business Details</h3>
                      {isEditingShop ? (
                        <div className="edit-form">
                          <div className="form-group">
                            <label className="form-label">Business Days</label>
                            <div className="checkbox-group">
                              {[
                                { label: 'Sunday', value: 0 },
                                { label: 'Monday', value: 1 },
                                { label: 'Tuesday', value: 2 },
                                { label: 'Wednesday', value: 3 },
                                { label: 'Thursday', value: 4 },
                                { label: 'Friday', value: 5 },
                                { label: 'Saturday', value: 6 },
                              ].map(({ label, value }) => (
                                <label key={value} className="filter-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={editShopData.businessDays?.includes(value)}
                                    onChange={(e) => {
                                      const days = editShopData.businessDays || [];

                                      if (e.target.checked) {
                                        setEditShopData(prev => ({
                                          ...prev,
                                          businessDays: [...days, value].sort((a, b) => a - b),
                                        }));
                                      } else {
                                        setEditShopData(prev => ({
                                          ...prev,
                                          businessDays: days.filter(d => d !== value),
                                        }));
                                      }
                                    }}
                                  />
                                  {label}
                                </label>
                              ))}
                            </div>

                          </div>
                        </div>
                      ) : (
                        <div className="detail-items">
                          <div className="detail-item">
                            <span className="label">Business Days:</span>
                            <span className="value">{selectedShop.businessDays?.join(', ') || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Rating & Engagement */}
                    <div className="detail-section">
                      <h3>Rating & Engagement</h3>
                      <div className="detail-items">
                        <div className="detail-item">
                          <span className="label">Rating:</span>
                          <span className="value">{selectedShop.rating?.toFixed(1) || 'N/A'} ({selectedShop.ratingCount || 0} reviews)</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Followers:</span>
                          <span className="value">{selectedShop.followersCount || 0}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Follow Requests:</span>
                          <span className="value">{selectedShop.followRequestsCount || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Status Information */}
                    <div className="detail-section">
                      <h3>Status Information</h3>
                      <div className="detail-items">
                        <div className="detail-item">
                          <span className="label">Status:</span>
                          <span className={`status-badge status-${selectedShop.status?.toLowerCase()}`}>
                            {selectedShop.status}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Verified:</span>
                          <span className={`status-badge ${selectedShop.isVerified ? 'status-approved' : 'status-pending'}`}>
                            {selectedShop.isVerified ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Active:</span>
                          <span className={`status-badge ${selectedShop.shopActive ? 'status-approved' : 'status-rejected'}`}>
                            {selectedShop.shopActive ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Categories Section */}
                  {selectedShop.categories && selectedShop.categories.length > 0 && (
                    <div className="categories-section">
                      <h3 className="section-title">Shop Categories</h3>
                      <div className="categories-list">
                        {selectedShop.categories.map(category => (
                          <div key={category._id} className="category-tag">
                            <span className="category-name">{category.name}</span>
                            <span className="category-level">Level {category.level}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shop Images Gallery */}
                  {selectedShop.shopImages && selectedShop.shopImages.length > 0 && (
                    <div className="gallery-section">
                      <h3 className="section-title">Shop Gallery</h3>
                      <div className="shop-images-grid">
                        {selectedShop.shopImages.map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Shop ${index + 1}`}
                            className="shop-image"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'fees-settings' && (
                <div className="shop-overview">
                  <div className="shop-details-grid">
                    <div className="detail-section">
                      <div className="section-header">
                        <h3>Shop fees & settings</h3>
                        {!isEditingShop ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={handleEditShop}
                            disabled={isEditingOwner}
                          >
                            Edit
                          </button>
                        ) : (
                          <span className="text-sm text-gray-500">Editing…</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500" style={{ marginBottom: 16 }}>
                        Delivery options, pickup, and fee fields for this shop.
                      </p>
                      {isEditingShop ? (
                        <div className="edit-form">
                          <div className="form-group full-width" style={{ marginBottom: 16 }}>
                            <label className="form-label">Delivery</label>
                            <div className="delivery-toggle-wrap">
                              <button
                                type="button"
                                className={`delivery-toggle-switch ${
                                  selectedShop.owner?.planType === 'F_MONTHLY'
                                    ? 'disabled is-disabled'
                                    : editShopData.enableDeliveryOption
                                      ? 'enabled'
                                      : 'disabled'
                                }`}
                                onClick={() => {
                                  if (selectedShop.owner?.planType === 'F_MONTHLY') return;
                                  setEditShopData(prev => ({ ...prev, enableDeliveryOption: !prev.enableDeliveryOption }));
                                }}
                                disabled={selectedShop.owner?.planType === 'F_MONTHLY'}
                                aria-pressed={!!editShopData.enableDeliveryOption}
                              >
                                <span className="delivery-toggle-knob" />
                              </button>
                              <span className="delivery-toggle-label">
                                {editShopData.enableDeliveryOption ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {selectedShop.owner?.planType === 'F_MONTHLY'
                                ? 'Delivery option is disabled for F_MONTHLY plan.'
                                : 'Choose whether delivery is enabled for this shop.'}
                            </span>
                          </div>

                          <div className="form-group full-width" style={{ marginBottom: 16 }}>
                            <label className="form-label">Delivery From</label>
                            <div className="delivery-toggle-wrap">
                              <button
                                type="button"
                                className={`delivery-toggle-switch ${
                                  selectedShop.owner?.planType === 'F_MONTHLY' || !editShopData.enableDeliveryOption
                                    ? 'is-disabled'
                                    : ''
                                } ${editShopData.deliveryFrom === 'KD' ? 'enabled' : 'disabled'}`}
                                onClick={() => {
                                  if (selectedShop.owner?.planType === 'F_MONTHLY') return;
                                  if (!editShopData.enableDeliveryOption) return;
                                  setEditShopData(prev => ({
                                    ...prev,
                                    deliveryFrom: prev.deliveryFrom === 'KD' ? 'SELF' : 'KD',
                                  }));
                                }}
                                disabled={
                                  selectedShop.owner?.planType === 'F_MONTHLY' || !editShopData.enableDeliveryOption
                                }
                                aria-pressed={editShopData.deliveryFrom === 'KD'}
                              >
                                <span className="delivery-toggle-knob" />
                              </button>
                              <span className="delivery-toggle-label">
                                {editShopData.deliveryFrom === 'KD' ? 'KD' : 'SELF'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {selectedShop.owner?.planType === 'F_MONTHLY'
                                ? 'Delivery is disabled for F_MONTHLY plan.'
                                : editShopData.enableDeliveryOption
                                  ? 'Choose whether orders are delivered by KD or by the shop.'
                                  : 'Enable delivery to select delivery source.'}
                            </span>
                          </div>

                          <div className="form-group full-width" style={{ marginBottom: 16 }}>
                            <label className="form-label">Pickup</label>
                            <div className="delivery-toggle-wrap">
                              <button
                                type="button"
                                className={`delivery-toggle-switch ${
                                  editShopData.enablePickupOption
                                    ? 'enabled'
                                    : 'disabled'
                                }`}
                                onClick={() => {
                                  setEditShopData(prev => ({ ...prev, enablePickupOption: !prev.enablePickupOption }));
                                }}
                                aria-pressed={!!editShopData.enablePickupOption}
                              >
                                <span className="delivery-toggle-knob" />
                              </button>
                              <span className="delivery-toggle-label">
                                {editShopData.enablePickupOption ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              Choose whether pickup is enabled for this shop.
                            </span>
                          </div>

                          <div className="form-group full-width" style={{ marginBottom: 16 }}>
                            <label className="form-label">Policies &amp; payment</label>
                            <div className="feature-toggles-grid">
                              {SHOP_POLICY_OPTIONS.map((f) => {
                                const isEnabled = editShopData?.[f.key] === true;
                                return (
                                  <div key={f.key} className="feature-toggle-card">
                                    <div className="feature-toggle-left">
                                      <div className={`feature-toggle-icon feature-toggle-icon--${f.variant}`}>
                                        {f.renderIcon ? f.renderIcon() : <span className="feature-fallback-symbol">{f.symbol}</span>}
                                      </div>
                                      <div className="feature-toggle-meta">
                                        <div className="feature-toggle-title">{f.label}</div>
                                      </div>
                                    </div>

                                    <div className="feature-toggle-switch">
                                      <button
                                        type="button"
                                        className={`delivery-toggle-switch ${isEnabled ? 'enabled' : 'disabled'}`}
                                        onClick={() =>
                                          setEditShopData(prev => ({ ...prev, [f.key]: !prev[f.key] }))
                                        }
                                        aria-pressed={isEnabled}
                                        aria-label={f.label}
                                      >
                                        <span className="delivery-toggle-knob" />
                                      </button>
                                      <span className="delivery-toggle-label">{isEnabled ? 'Enabled' : 'Disabled'}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="form-grid">
                            <div className="form-group">
                              <label className="form-label">Base Delivery Fee (₹)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editShopData.baseDeliveryFee}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, baseDeliveryFee: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Base Distance (km)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={editShopData.baseDeliveryDistanceKm}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, baseDeliveryDistanceKm: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Per KM Fee (₹)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editShopData.perKmDeliveryFee}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, perKmDeliveryFee: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Max Distance (km)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={editShopData.maxDeliveryDistanceKm}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, maxDeliveryDistanceKm: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Minimum order value</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editShopData.freeDeliveryMinOrderValue}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, freeDeliveryMinOrderValue: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Minimum order value for free delivery</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editShopData.freeDeliveryMinOrderValue2}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, freeDeliveryMinOrderValue2: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Shipping Charges (₹)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editShopData.shippingCharges}
                                onChange={(e) => setEditShopData(prev => ({ ...prev, shippingCharges: e.target.value }))}
                                className="form-input"
                              />
                            </div>
                          </div>
                          <div className="form-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={handleSaveShop}
                              disabled={loading}
                            >
                              {loading ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-items">
                          <div className="detail-item">
                            <span className="label">
                              Delivery
                              {selectedShop.owner?.planType === 'F_MONTHLY' && (
                                <span className="text-xs text-gray-500" style={{ marginLeft: 8 }}>
                                  (Disabled for F_MONTHLY plan)
                                </span>
                              )}
                            </span>
                            <span className="value">
                              <div className="delivery-toggle-wrap">
                                <button
                                  type="button"
                                  className={`delivery-toggle-switch ${
                                    selectedShop.owner?.planType === 'F_MONTHLY'
                                      ? 'disabled is-disabled'
                                      : selectedShop.enableDeliveryOption === false
                                        ? 'disabled'
                                        : 'enabled'
                                  } ${togglingDelivery ? 'is-disabled' : ''}`}
                                  onClick={handleToggleDelivery}
                                  disabled={togglingDelivery || selectedShop.owner?.planType === 'F_MONTHLY'}
                                  title={selectedShop.enableDeliveryOption === false ? 'Enable delivery' : 'Disable delivery'}
                                  aria-pressed={selectedShop.enableDeliveryOption !== false}
                                >
                                  <span className="delivery-toggle-knob" />
                                </button>
                                <span className="delivery-toggle-label">
                                  {selectedShop.enableDeliveryOption === false ? 'Disabled' : 'Enabled'}
                                </span>
                              </div>
                              {togglingDelivery && (
                                <span className="text-xs text-gray-500" style={{ marginLeft: 8 }}>Updating…</span>
                              )}
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Delivery From:</span>
                            <span className="value">
                              <div className="delivery-toggle-wrap">
                                <button
                                  type="button"
                                  className={`delivery-toggle-switch ${
                                    selectedShop.owner?.planType === 'F_MONTHLY' || selectedShop.enableDeliveryOption === false
                                      ? 'is-disabled'
                                      : ''
                                  } ${selectedShop.deliveryFrom === 'KD' ? 'enabled' : 'disabled'} ${
                                    togglingDeliveryFrom ? 'is-disabled' : ''
                                  }`}
                                  onClick={handleToggleDeliveryFrom}
                                  disabled={
                                    togglingDeliveryFrom ||
                                    selectedShop.owner?.planType === 'F_MONTHLY' ||
                                    selectedShop.enableDeliveryOption === false
                                  }
                                  aria-pressed={selectedShop.deliveryFrom === 'KD'}
                                  title={selectedShop.deliveryFrom === 'KD' ? 'Switch to SELF' : 'Switch to KD'}
                                >
                                  <span className="delivery-toggle-knob" />
                                </button>
                                <span className="delivery-toggle-label">
                                  {selectedShop.deliveryFrom === 'KD' ? 'KD' : 'SELF'}
                                </span>
                              </div>
                              {togglingDeliveryFrom && (
                                <span className="text-xs text-gray-500" style={{ marginLeft: 8 }}>Updating…</span>
                              )}
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Pickup</span>
                            <span className="value">
                              <div className="delivery-toggle-wrap">
                                <button
                                  type="button"
                                  className={`delivery-toggle-switch ${
                                    selectedShop.enablePickupOption === false
                                      ? 'disabled'
                                      : 'enabled'
                                  } ${togglingPickup ? 'is-disabled' : ''}`}
                                  onClick={handleTogglePickup}
                                  disabled={togglingPickup}
                                  title={selectedShop.enablePickupOption === false ? 'Enable pickup' : 'Disable pickup'}
                                  aria-pressed={selectedShop.enablePickupOption !== false}
                                >
                                  <span className="delivery-toggle-knob" />
                                </button>
                                <span className="delivery-toggle-label">
                                  {selectedShop.enablePickupOption === false ? 'Disabled' : 'Enabled'}
                                </span>
                              </div>
                              {togglingPickup && (
                                <span className="text-xs text-gray-500" style={{ marginLeft: 8 }}>Updating…</span>
                              )}
                            </span>
                          </div>

                          <div className="coming-soon-features">
                            <div className="coming-soon-features-title">Policies &amp; payment</div>
                            <div className="feature-toggles-grid">
                              {SHOP_POLICY_OPTIONS.map((f) => {
                                const isEnabled = selectedShop?.[f.key] === true;
                                const isToggling = togglingPolicyOption === f.key;
                                return (
                                  <div key={f.key} className="feature-toggle-card">
                                    <div className="feature-toggle-left">
                                      <div className={`feature-toggle-icon feature-toggle-icon--${f.variant}`}>
                                        {f.renderIcon ? f.renderIcon() : <span className="feature-fallback-symbol">{f.symbol}</span>}
                                      </div>
                                      <div className="feature-toggle-meta">
                                        <div className="feature-toggle-title">{f.label}</div>
                                      </div>
                                    </div>

                                    <div className="feature-toggle-switch">
                                      <button
                                        type="button"
                                        className={`delivery-toggle-switch ${isEnabled ? 'enabled' : 'disabled'} ${
                                          isToggling ? 'is-disabled' : ''
                                        }`}
                                        onClick={() => handleTogglePolicyOption(f.key)}
                                        disabled={isToggling}
                                        aria-pressed={isEnabled}
                                        aria-label={f.label}
                                      >
                                        <span className="delivery-toggle-knob" />
                                      </button>
                                      <span className="delivery-toggle-label">
                                        {isToggling ? 'Updating…' : isEnabled ? 'Enabled' : 'Disabled'}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="detail-item">
                            <span className="label">Base Delivery Fee:</span>
                            <span className="value">₹{selectedShop.baseDeliveryFee || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Base Distance:</span>
                            <span className="value">{selectedShop.baseDeliveryDistanceKm || 0} km</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Per KM Fee:</span>
                            <span className="value">₹{selectedShop.perKmDeliveryFee || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Max Distance:</span>
                            <span className="value">{selectedShop.maxDeliveryDistanceKm || 0} km</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Minimum order value:</span>
                            <span className="value">₹{selectedShop.freeDeliveryMinOrderValue || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Minimum order value for free delivery:</span>
                            <span className="value">₹{selectedShop.freeDeliveryMinOrderValue2 || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Shipping Charges:</span>
                            <span className="value">₹{selectedShop.shippingCharges || 0}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'analytics' && (
                <div className="analytics-tab">
                  {loadingAnalytics ? (
                    <div className="loading-analytics">
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading analytics...</p>
                      </div>
                    </div>
                  ) : !analytics ? (
                    <div className="no-analytics">
                      <div className="empty-state">
                        <div className="empty-icon">📊</div>
                        <div className="empty-title">No Analytics Data</div>
                        <div className="empty-description">
                          Analytics data for {selectedShop.name} is not available at the moment.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="shop-analytics">
                      {/* Key Metrics Overview */}
                      <div className="analytics-overview">
                        <div className="metric-card revenue">
                          <div className="metric-icon">₹</div>
                          <div className="metric-content">
                            <div className="metric-value">₹{analytics.revenue?.totalRevenue?.toLocaleString() || 0}</div>
                            <div className="metric-label">Total Revenue</div>
                            <div className="metric-sub">Avg: ₹{analytics.revenue?.avgOrderValue?.toFixed(0) || 0} per order</div>
                          </div>
                        </div>

                        <div className="metric-card orders">
                          <div className="metric-icon">📦</div>
                          <div className="metric-content">
                            <div className="metric-value">{analytics.orders?.totalOrders || 0}</div>
                            <div className="metric-label">Total Orders</div>
                            <div className="metric-sub">{analytics.orders?.totalOrders > 0 ? ((analytics.orders?.completedOrders || 0) / analytics.orders.totalOrders * 100).toFixed(1) : 0}% completion rate</div>
                          </div>
                        </div>

                        <div className="metric-card customers">
                          <div className="metric-icon">👥</div>
                          <div className="metric-content">
                            <div className="metric-value">{analytics.customers?.totalCustomers || 0}</div>
                            <div className="metric-label">Total Customers</div>
                            <div className="metric-sub">{analytics.followers || 0} followers</div>
                          </div>
                        </div>

                        <div className="metric-card products">
                          <div className="metric-icon">📋</div>
                          <div className="metric-content">
                            <div className="metric-value">{analytics.products?.totalProducts || 0}</div>
                            <div className="metric-label">Total Products</div>
                            <div className="metric-sub">{analytics.products?.totalProducts > 0 ? ((analytics.products?.activeProducts || 0) / analytics.products.totalProducts * 100).toFixed(1) : 0}% active</div>
                          </div>
                        </div>
                      </div>

                      {/* Detailed Analytics */}
                      <div className="analytics-detailed">
                        {/* Products Analytics */}
                        <div className="analytics-section">
                          <h3>Product Analytics</h3>
                          <div className="chart-container">
                            <div className="chart-item">
                              <div className="chart-label">Active Products</div>
                              <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${analytics.products?.totalProducts > 0 ? (analytics.products?.activeProducts || 0) / analytics.products.totalProducts * 100 : 0}%` }}></div>
                              </div>
                              <div className="chart-value">{analytics.products?.activeProducts || 0} / {analytics.products?.totalProducts || 0}</div>
                            </div>
                            <div className="chart-item">
                              <div className="chart-label">Approved Products</div>
                              <div className="progress-bar">
                                <div className="progress-fill approved" style={{ width: `${analytics.products?.totalProducts > 0 ? (analytics.products?.approvedProducts || 0) / analytics.products.totalProducts * 100 : 0}%` }}></div>
                              </div>
                              <div className="chart-value">{analytics.products?.approvedProducts || 0} / {analytics.products?.totalProducts || 0}</div>
                            </div>
                            <div className="stat-highlight">
                              <span className="stat-number">₹{analytics.products?.avgPrice?.toFixed(0) || 0}</span>
                              <span className="stat-desc">Average Product Price</span>
                            </div>
                          </div>
                        </div>

                        {/* Orders Analytics */}
                        <div className="analytics-section">
                          <h3>Order Analytics</h3>
                          <div className="chart-container">
                            <div className="order-breakdown">
                              <div className="order-stat completed">
                                <div className="order-count">{analytics.orders?.completedOrders || 0}</div>
                                <div className="order-label">Completed</div>
                              </div>
                              <div className="order-stat pending">
                                <div className="order-count">{analytics.orders?.pendingOrders || 0}</div>
                                <div className="order-label">Pending</div>
                              </div>
                              <div className="order-stat cancelled">
                                <div className="order-count">{analytics.orders?.cancelledOrders || 0}</div>
                                <div className="order-label">Cancelled</div>
                              </div>
                            </div>
                            <div className="completion-rate">
                              <div className="rate-circle">
                                <div className="rate-number">{analytics.orders?.totalOrders > 0 ? ((analytics.orders?.completedOrders || 0) / analytics.orders.totalOrders * 100).toFixed(1) : 0}%</div>
                                <div className="rate-label">Success Rate</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Customer Analytics */}
                        <div className="analytics-section">
                          <h3>Customer Analytics</h3>
                          <div className="chart-container">
                            <div className="customer-metrics">
                              <div className="customer-metric">
                                <div className="metric-bar">
                                  <div className="bar-fill" style={{ height: `${analytics.customers?.totalCustomers > 0 ? (analytics.customers?.activeCustomers || 0) / analytics.customers.totalCustomers * 100 : 0}%` }}></div>
                                </div>
                                <div className="metric-info">
                                  <div className="metric-num">{analytics.customers?.activeCustomers || 0}</div>
                                  <div className="metric-text">Active</div>
                                </div>
                              </div>
                              <div className="customer-metric">
                                <div className="metric-bar">
                                  <div className="bar-fill followers" style={{ height: `${analytics.customers?.totalCustomers > 0 ? (analytics.followers || 0) / analytics.customers.totalCustomers * 100 : 0}%` }}></div>
                                </div>
                                <div className="metric-info">
                                  <div className="metric-num">{analytics.followers || 0}</div>
                                  <div className="metric-text">Followers</div>
                                </div>
                              </div>
                              <div className="customer-metric">
                                <div className="metric-bar">
                                  <div className="bar-fill reviews" style={{ height: `${analytics.customers?.totalCustomers > 0 ? Math.min((analytics.ratings?.totalReviews || 0) / analytics.customers.totalCustomers * 100, 100) : 0}%` }}></div>
                                </div>
                                <div className="metric-info">
                                  <div className="metric-num">{analytics.ratings?.totalReviews || 0}</div>
                                  <div className="metric-text">Reviews</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'products' && (
                <div className="products-tab">
                  {/* Product Filters */}
                  <div className="filters-toolbar">
                    <div className="filter-group">
                      <label className="filter-label">Product Search</label>
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productSearch}
                        onChange={(e) => {
                          setProductPage(1);
                          setProductSearch(e.target.value);
                        }}
                        className="filter-input search-input"
                      />
                    </div>

                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={!!approved}
                        onChange={(e) => setApproved(e.target.checked ? true : undefined)}
                      />
                      Approved Only
                    </label>

                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                      />
                      Include Inactive
                    </label>

                    <div className="filter-group">
                      <label className="filter-label">Sort</label>
                      <select
                        value={productSort}
                        onChange={(e) => setProductSort(e.target.value)}
                        className="filter-select"
                      >
                        <option value="createdAt">Created Date</option>
                        <option value="price">Price</option>
                        <option value="ratings.average">Rating</option>
                      </select>
                    </div>

                    <div className="filter-group">
                      <select
                        value={productOrder}
                        onChange={(e) => setProductOrder(e.target.value)}
                        className="filter-select"
                      >
                        <option value="desc">Descending</option>
                        <option value="asc">Ascending</option>
                      </select>
                    </div>

                    <div className="filter-group">
                      <label className="filter-label">Show</label>
                      <select
                        value={productLimit}
                        onChange={(e) => {
                          setProductPage(1);
                          setProductLimit(parseInt(e.target.value || "20", 10));
                        }}
                        className="filter-select"
                      >
                        <option value={10}>10 rows</option>
                        <option value={20}>20 rows</option>
                        <option value={50}>50 rows</option>
                        <option value={100}>100 rows</option>
                      </select>
                    </div>

                    <div className="toolbar-divider"></div>

                    <button
                      onClick={() => {
                        if (!isCreatingProduct) {
                          resetProductForm();
                          setIsCreatingProduct(true);
                        } else {
                          setIsCreatingProduct(false);
                          resetProductForm();
                        }
                      }}
                      className="btn btn-primary"
                    >
                      {isCreatingProduct ? "Cancel" : "New Product"}
                    </button>
                  </div>

                  {/* Product Creation/Edit Form */}
                  {isCreatingProduct && (
                    <div className="form-modal" onClick={() => { resetProductForm(); setIsCreatingProduct(false); }}>
                      <div className="form-container" onClick={(e) => e.stopPropagation()}>
                        <div className="form-header">
                          <h2 className="form-title">{editingProductId ? 'Edit Product' : 'Create New Product'}</h2>
                          <button className="form-close" onClick={() => { resetProductForm(); setIsCreatingProduct(false); }}>×</button>
                        </div>

                        <div className="form-content">
                          <div className="form-section">
                            <h3 className="form-section-title">Basic Information</h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label">Product Name *</label>
                                <input
                                  type="text"
                                  value={productFormData.name}
                                  onChange={(e) => onChangeProductField("name", e.target.value)}
                                  className="form-input"
                                  placeholder="Enter product name"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Price * (₹)</label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={productFormData.price}
                                  onChange={(e) => onChangeProductField("price", e.target.value)}
                                  className="form-input"
                                  placeholder="0.00"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">MRP (₹)</label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={productFormData.mrp}
                                  onChange={(e) => onChangeProductField("mrp", e.target.value)}
                                  className="form-input"
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Category *</label>
                                <CategoryTreeSelect
                                  options={categories}
                                  value={productFormData.category}
                                  onChange={(v) => onChangeProductField('category', v)}
                                  placeholder={loadingCategories ? 'Loading categories...' : 'Select category'}
                                  disabled={loadingCategories}
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Tags</label>
                                <input
                                  type="text"
                                  value={productFormData.tags}
                                  onChange={(e) => onChangeProductField("tags", e.target.value)}
                                  className="form-input"
                                  placeholder="tag1, tag2, tag3"
                                />
                              </div>
                              <div className="form-group full-width">
                                <label className="form-label">Description</label>
                                <textarea
                                  value={productFormData.description}
                                  onChange={(e) => onChangeProductField("description", e.target.value)}
                                  className="form-textarea"
                                  rows={3}
                                  placeholder="Enter product description"
                                />
                              </div>
                              <div className="form-group full-width">
                                <label className="form-label">Image URLs</label>
                                <textarea
                                  value={(productFormData.image || []).join("\n")}
                                  onChange={(e) =>
                                    onChangeProductField(
                                      "image",
                                      e.target.value
                                        .split("\n")
                                        .map((s) => s.trim())
                                        .filter(Boolean)
                                    )
                                  }
                                  className="form-textarea"
                                  rows={3}
                                  placeholder="Enter image URLs (one per line)"
                                />
                              </div>
                              <div className="form-group">
                                <label className="filter-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={productFormData.sponsored}
                                    onChange={(e) => onChangeProductField("sponsored", e.target.checked)}
                                  />
                                  Sponsored Product
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="form-footer">
                          <button className="btn btn-secondary" onClick={() => { resetProductForm(); setIsCreatingProduct(false); }}>
                            Cancel
                          </button>
                          {editingProductId ? (
                            <button
                              disabled={!canSubmitProduct || loading}
                              onClick={handleUpdateProduct}
                              className="btn btn-primary"
                            >
                              {loading ? 'Updating...' : 'Update Product'}
                            </button>
                          ) : (
                            <button
                              disabled={!canSubmitProduct || loading}
                              onClick={handleCreateProduct}
                              className="btn btn-success"
                            >
                              {loading ? 'Creating...' : 'Create Product'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Products Table */}
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>MRP</th>
                          <th>Category</th>
                          <th>Approved</th>
                          <th>Active</th>
                          <th>Rating</th>
                          <th>Tags</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="10">
                              <div className="loading-state">
                                <div className="loading-spinner"></div>
                                <p>Loading products...</p>
                              </div>
                            </td>
                          </tr>
                        ) : products.length === 0 ? (
                          <tr>
                            <td colSpan="10">
                              <div className="empty-state">
                                <div className="empty-icon">📦</div>
                                <div className="empty-title">No products found</div>
                                <div className="empty-description">
                                  This shop doesn't have any products yet. Create one to get started!
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          products.map((p) => (
                            <tr key={p._id}>
                              <td className="font-semibold" title={p.name}>
                                {p.name.length > 30 ? `${p.name.substring(0, 30)}...` : p.name}
                              </td>
                              <td>₹{p.price}</td>
                              <td>
                                {p.mrp != null && p.mrp !== "" ? `₹${p.mrp}` : "—"}
                              </td>
                              <td title={p.category?.name || p.category}>
                                {p.category?.name || String(p.category).slice(0, 10)}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${p.isApproved ? "status-approved" : "status-pending"
                                    }`}
                                >
                                  {p.isApproved ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${p.isActive ? "status-approved" : "status-rejected"
                                    }`}
                                >
                                  {p.isActive ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                ⭐ {p.ratings?.average?.toFixed(1) ?? 0} ({p.ratings?.totalReviews ?? 0})
                              </td>
                              <td title={(p.tags || []).join(", ")}>
                                {(p.tags || []).slice(0, 2).join(", ")}
                                {(p.tags || []).length > 2 && "..."}
                              </td>
                              <td>
                                {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "-"}
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => populateProductFormForEdit(p)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteProduct(p._id)}
                                  >
                                    Delete
                                  </button>
                                  <button
                                    className={`btn btn-sm ${p.isApproved ? 'btn-secondary' : 'btn-success'}`}
                                    onClick={() => toggleProductApproval(p)}
                                  >
                                    {p.isApproved ? "Unapprove" : "Approve"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Product Pagination */}
                  <div className="pagination">
                    <div className="pagination-info">
                      Page {productPage} of {productTotalPages}
                    </div>
                    <div className="pagination-controls">
                      <button
                        onClick={() => setProductPage((p) => Math.max(1, p - 1))}
                        disabled={productPage <= 1}
                        className="pagination-btn"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setProductPage((p) => Math.min(productTotalPages, p + 1))}
                        disabled={productPage >= productTotalPages}
                        className="pagination-btn"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'shop-members' && (
                <div className="shop-staff-tab">
                  <div className="shop-staff-header">
                    <h3>Shop Members</h3>
                    <p>Team members assigned to this shop</p>
                  </div>
                  {(selectedShop.shopMembers || []).length === 0 ? (
                    <div className="empty-state">
                      <p>No shop members assigned to this shop.</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Member</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Position</th>
                            <th>Permissions</th>
                            <th>Status</th>
                            <th>Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedShop.shopMembers || []).map((member) => (
                            <tr key={member._id}>
                              <td>
                                <div className="staff-user-cell">
                                  <img
                                    src={member.user?.profileImageUrl || '/KD-icon.png'}
                                    alt={member.user?.fullName || 'Member'}
                                    className="staff-avatar"
                                    onError={(e) => { e.target.src = '/KD-icon.png'; }}
                                  />
                                  <span>{member.user?.fullName || 'N/A'}</span>
                                </div>
                              </td>
                              <td>{member.user?.phone || 'N/A'}</td>
                              <td>{member.user?.email || 'N/A'}</td>
                              <td>{member.position || '—'}</td>
                              <td>
                                <div className="permission-chips">
                                  {member.canAddProduct && <span className="status-badge status-pending">Product</span>}
                                  {member.canAddVideo && <span className="status-badge status-pending">Video</span>}
                                  {member.canAddCoupon && <span className="status-badge status-pending">Coupon</span>}
                                  {member.canBookOrder && <span className="status-badge status-pending">Book Order</span>}
                                  {!member.canAddProduct && !member.canAddVideo && !member.canAddCoupon && !member.canBookOrder && (
                                    <span className="text-xs text-gray-500">None</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className={`status-badge ${member.accountStatus === 'ACTIVE' ? 'status-approved' : 'status-rejected'}`}>
                                  {member.accountStatus || 'N/A'}
                                </span>
                              </td>
                              <td>
                                {member.joiningDate
                                  ? new Date(member.joiningDate).toLocaleDateString()
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'delivery-boys' && (
                <div className="shop-staff-tab">
                  <div className="shop-staff-header">
                    <h3>Delivery Boys</h3>
                    <p>Delivery partners assigned to this shop</p>
                  </div>
                  {(selectedShop.deliveryBoys || []).length === 0 ? (
                    <div className="empty-state">
                      <p>No delivery boys assigned to this shop.</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Delivery Boy</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Position</th>
                            <th>Skills</th>
                            <th>Status</th>
                            <th>Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedShop.deliveryBoys || []).map((boy) => (
                            <tr key={boy._id}>
                              <td>
                                <div className="staff-user-cell">
                                  <img
                                    src={boy.user?.profileImageUrl || '/KD-icon.png'}
                                    alt={boy.user?.fullName || 'Delivery boy'}
                                    className="staff-avatar"
                                    onError={(e) => { e.target.src = '/KD-icon.png'; }}
                                  />
                                  <span>{boy.user?.fullName || 'N/A'}</span>
                                </div>
                              </td>
                              <td>{boy.user?.phone || 'N/A'}</td>
                              <td>{boy.user?.email || 'N/A'}</td>
                              <td>{boy.position || '—'}</td>
                              <td>
                                {(boy.skills || []).length > 0
                                  ? boy.skills.join(', ')
                                  : '—'}
                              </td>
                              <td>
                                <span className={`status-badge ${boy.accountStatus === 'ACTIVE' ? 'status-approved' : 'status-rejected'}`}>
                                  {boy.accountStatus || 'N/A'}
                                </span>
                              </td>
                              <td>
                                {boy.joiningDate
                                  ? new Date(boy.joiningDate).toLocaleDateString()
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'field-boys' && (
                <div className="shop-staff-tab">
                  <div className="shop-staff-header">
                    <h3>Field Boy</h3>
                    <p>Field partner assigned to this shop</p>
                  </div>
                  {(selectedShop.fieldBoys || []).length === 0 ? (
                    <div className="empty-state">
                      <p>No field boy assigned to this shop.</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Field Boy</th>
                            <th>Code</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Zone</th>
                            <th>Skills</th>
                            <th>Status</th>
                            <th>Assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedShop.fieldBoys || []).map((boy) => (
                            <tr key={boy._id}>
                              <td>
                                <div className="staff-user-cell">
                                  <img
                                    src={boy.user?.profileImageUrl || '/KD-icon.png'}
                                    alt={boy.user?.fullName || 'Field boy'}
                                    className="staff-avatar"
                                    onError={(e) => { e.target.src = '/KD-icon.png'; }}
                                  />
                                  <span>{boy.user?.fullName || 'N/A'}</span>
                                </div>
                              </td>
                              <td>{boy.employeeCode || '—'}</td>
                              <td>{boy.user?.phone || 'N/A'}</td>
                              <td>{boy.user?.email || 'N/A'}</td>
                              <td>{boy.zone || '—'}</td>
                              <td>
                                {(boy.skills || []).length > 0
                                  ? boy.skills.join(', ')
                                  : '—'}
                              </td>
                              <td>
                                <span className={`status-badge ${['ACTIVE', 'ACCEPTED'].includes(boy.accountStatus) ? 'status-approved' : 'status-rejected'}`}>
                                  {boy.accountStatus || 'N/A'}
                                </span>
                              </td>
                              <td>
                                {boy.assignedAt
                                  ? new Date(boy.assignedAt).toLocaleDateString()
                                  : boy.joiningDate
                                    ? new Date(boy.joiningDate).toLocaleDateString()
                                    : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'bloggers' && (
                <div className="shop-staff-tab">
                  <div className="shop-staff-header">
                    <h3>Blogger</h3>
                    <p>Blogger assigned to this shop</p>
                  </div>
                  {(selectedShop.bloggers || []).length === 0 ? (
                    <div className="empty-state">
                      <p>No blogger assigned to this shop.</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Blogger</th>
                            <th>Code</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Zone</th>
                            <th>Niches</th>
                            <th>Status</th>
                            <th>Assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedShop.bloggers || []).map((blogger) => (
                            <tr key={blogger._id}>
                              <td>
                                <div className="staff-user-cell">
                                  <img
                                    src={blogger.user?.profileImageUrl || '/KD-icon.png'}
                                    alt={blogger.user?.fullName || 'Blogger'}
                                    className="staff-avatar"
                                    onError={(e) => { e.target.src = '/KD-icon.png'; }}
                                  />
                                  <span>{blogger.user?.fullName || 'N/A'}</span>
                                </div>
                              </td>
                              <td>{blogger.employeeCode || '—'}</td>
                              <td>{blogger.user?.phone || 'N/A'}</td>
                              <td>{blogger.user?.email || 'N/A'}</td>
                              <td>{blogger.zone || '—'}</td>
                              <td>
                                {(blogger.niches || []).length > 0
                                  ? blogger.niches.join(', ')
                                  : '—'}
                              </td>
                              <td>
                                <span className={`status-badge ${['ACTIVE', 'ACCEPTED'].includes(blogger.accountStatus) ? 'status-approved' : 'status-rejected'}`}>
                                  {blogger.accountStatus || 'N/A'}
                                </span>
                              </td>
                              <td>
                                {blogger.assignedAt
                                  ? new Date(blogger.assignedAt).toLocaleDateString()
                                  : blogger.joiningDate
                                    ? new Date(blogger.joiningDate).toLocaleDateString()
                                    : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'offers' && (
                <div className="offers-tab">
                  {/* Coupon Filters */}
                  <div className="filters-toolbar">
                    <div className="filter-group">
                      <label className="filter-label">Type</label>
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Types</option>
                        <option value="percentage">Percentage</option>
                        <option value="flat">Flat Discount</option>
                        <option value="buyxgety">Buy X Get Y</option>
                      </select>
                    </div>

                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={!!filterCustomerOnly}
                        onChange={(e) => setFilterCustomerOnly(e.target.checked ? true : undefined)}
                      />
                      Customer Only
                    </label>

                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={!!filterOnlineOnly}
                        onChange={(e) => setFilterOnlineOnly(e.target.checked ? true : undefined)}
                      />
                      Online Only
                    </label>

                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={!!filterActive}
                        onChange={(e) => setFilterActive(e.target.checked ? true : undefined)}
                      />
                      Active
                    </label>

                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={!!filterExpired}
                        onChange={(e) => setFilterExpired(e.target.checked ? true : undefined)}
                      />
                      Expired
                    </label>

                    <div className="toolbar-divider"></div>

                    <button
                      onClick={() => {
                        if (!isCreatingCoupon) {
                          resetCouponForm();
                          setIsCreatingCoupon(true);
                        } else {
                          setIsCreatingCoupon(false);
                          resetCouponForm();
                        }
                      }}
                      className="btn btn-primary"
                    >
                      {isCreatingCoupon ? "Cancel" : "New Coupon"}
                    </button>
                  </div>

                  {/* Coupon Creation/Edit Form */}
                  {isCreatingCoupon && (
                    <div className="form-modal" onClick={() => { resetCouponForm(); setIsCreatingCoupon(false); }}>
                      <div className="form-container" onClick={(e) => e.stopPropagation()}>
                        <div className="form-header">
                          <h2 className="form-title">{editingCouponCode ? 'Edit Coupon' : 'Create New Coupon'}</h2>
                          <button className="form-close" onClick={() => { resetCouponForm(); setIsCreatingCoupon(false); }}>×</button>
                        </div>

                        <div className="form-content">
                          <div className="form-section">
                            <h3 className="form-section-title">Basic Information</h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label">Coupon Code *</label>
                                <input
                                  type="text"
                                  value={couponFormData.couponCode}
                                  onChange={(e) => onChangeCouponField("couponCode", e.target.value.toUpperCase())}
                                  disabled={!!editingCouponCode}
                                  className="form-input"
                                  placeholder="Enter coupon code"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Discount Type *</label>
                                <select
                                  value={couponFormData.type}
                                  onChange={(e) => onChangeCouponField("type", e.target.value)}
                                  className="form-select"
                                >
                                  <option value="percentage">Percentage Discount</option>
                                  <option value="flat">Flat Amount Discount</option>
                                  <option value="buyxgety">Buy X Get Y Free</option>
                                </select>
                              </div>

                              {(couponFormData.type === "percentage" || couponFormData.type === "flat") && (
                                <div className="form-group">
                                  <label className="form-label">Discount Value * {couponFormData.type === "percentage" ? "(%)" : "(₹)"}</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={couponFormData.discount}
                                    onChange={(e) => onChangeCouponField("discount", e.target.value)}
                                    className="form-input"
                                    placeholder={couponFormData.type === "percentage" ? "e.g., 10" : "e.g., 100"}
                                  />
                                </div>
                              )}

                              {couponFormData.type === "percentage" && (
                                <div className="form-group">
                                  <label className="form-label">Max Discount Amount * (₹)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={couponFormData.maxDiscount}
                                    onChange={(e) => onChangeCouponField("maxDiscount", e.target.value)}
                                    className="form-input"
                                    placeholder="e.g., 500"
                                  />
                                </div>
                              )}

                              {couponFormData.type === "buyxgety" && (
                                <>
                                  <div className="form-group">
                                    <label className="form-label">Buy Quantity *</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={couponFormData.buyQuantity}
                                      onChange={(e) => onChangeCouponField("buyQuantity", e.target.value)}
                                      className="form-input"
                                      placeholder="e.g., 2"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label className="form-label">Get Free Quantity *</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={couponFormData.getFreeQuantity}
                                      onChange={(e) => onChangeCouponField("getFreeQuantity", e.target.value)}
                                      className="form-input"
                                      placeholder="e.g., 1"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="form-section">
                            <h3 className="form-section-title">Order Constraints</h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label">Min Order Value (₹)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={couponFormData.minOrderValue}
                                  onChange={(e) => onChangeCouponField("minOrderValue", e.target.value)}
                                  className="form-input"
                                  placeholder="e.g., 500"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Max Order Value (₹)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={couponFormData.maxOrderValue}
                                  onChange={(e) => onChangeCouponField("maxOrderValue", e.target.value)}
                                  className="form-input"
                                  placeholder="e.g., 5000"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Uses Per Customer</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={couponFormData.usesPerCustomer}
                                  onChange={(e) => onChangeCouponField("usesPerCustomer", parseInt(e.target.value || "1", 10))}
                                  className="form-input"
                                  placeholder="1"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="form-section">
                            <h3 className="form-section-title">Validity Period</h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label">Valid From *</label>
                                <input
                                  type="datetime-local"
                                  value={couponFormData.validFrom}
                                  onChange={(e) => onChangeCouponField("validFrom", e.target.value)}
                                  className="form-input"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Valid To *</label>
                                <input
                                  type="datetime-local"
                                  value={couponFormData.validTo}
                                  onChange={(e) => onChangeCouponField("validTo", e.target.value)}
                                  className="form-input"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="form-section">
                            <h3 className="form-section-title">Options</h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="filter-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={couponFormData.showToCustomerOnly}
                                    onChange={(e) => onChangeCouponField("showToCustomerOnly", e.target.checked)}
                                  />
                                  Show To Customer Only
                                </label>
                              </div>
                              <div className="form-group">
                                <label className="filter-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={couponFormData.validForOnlinePaymentsOnly}
                                    onChange={(e) => onChangeCouponField("validForOnlinePaymentsOnly", e.target.checked)}
                                  />
                                  Valid For Online Payments Only
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="form-footer">
                          <button className="btn btn-secondary" onClick={() => { resetCouponForm(); setIsCreatingCoupon(false); }}>
                            Cancel
                          </button>
                          {editingCouponCode ? (
                            <button
                              disabled={!canSubmitCoupon() || loading}
                              onClick={handleUpdateCoupon}
                              className="btn btn-primary"
                            >
                              {loading ? 'Updating...' : 'Update Coupon'}
                            </button>
                          ) : (
                            <button
                              disabled={!canSubmitCoupon() || loading}
                              onClick={handleCreateCoupon}
                              className="btn btn-success"
                            >
                              {loading ? 'Creating...' : 'Create Coupon'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Coupons Table */}
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Type</th>
                          <th>Discount</th>
                          <th>Max Discount</th>
                          <th>Min Order</th>
                          <th>Max Order</th>
                          <th>Uses</th>
                          <th>Valid From</th>
                          <th>Valid To</th>
                          <th>Flags</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="11">
                              <div className="loading-state">
                                <div className="loading-spinner"></div>
                                <p>Loading coupons...</p>
                              </div>
                            </td>
                          </tr>
                        ) : coupons.length === 0 ? (
                          <tr>
                            <td colSpan="11">
                              <div className="empty-state">
                                <div className="empty-icon">🎫</div>
                                <div className="empty-title">No coupons found</div>
                                <div className="empty-description">
                                  This shop doesn't have any coupons yet. Create one to get started!
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          coupons.map((c) => (
                            <tr key={c._id}>
                              <td className="font-semibold">{c.couponCode}</td>
                              <td>
                                <span className="status-badge status-default">{c.type}</span>
                              </td>
                              <td>
                                {c.type === "buyxgety"
                                  ? `Buy ${c.buyQuantity} Get ${c.getFreeQuantity}`
                                  : `${c.discount}${c.type === "percentage" ? "%" : "₹"}`}
                              </td>
                              <td>{c.type === "percentage" ? `₹${c.maxDiscount ?? "-"}` : '-'}</td>
                              <td>₹{c.minOrderValue ?? 0}</td>
                              <td>₹{c.maxOrderValue ?? '-'}</td>
                              <td>{c.usesPerCustomer}</td>
                              <td>{new Date(c.validFrom).toLocaleDateString()}</td>
                              <td>{new Date(c.validTo).toLocaleDateString()}</td>
                              <td>
                                <div className="flex gap-2">
                                  {c.showToCustomerOnly && <span className="status-badge status-pending">Customer</span>}
                                  {c.validForOnlinePaymentsOnly && <span className="status-badge status-pending">Online</span>}
                                </div>
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => populateCouponFormForEdit(c)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteCoupon(c.couponCode)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirm Modal for Shop Deletion - Rendered at document body level */}
      {confirmOpen && createPortal(
        <ConfirmModal
          open={confirmOpen}
          title={shopToDelete ? `Delete shop "${shopToDelete.name}"?` : 'Delete shop'}
          message={shopToDelete ? `This will permanently remove all related data: announcements, coupons, team members, requests, products and related data, and bookings. This action cannot be undone.` : ''}
          confirmText="Delete"
          cancelText="Cancel"
          loading={loading}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />,
        document.body
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-icon">
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={hideToast}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopManagement;